import {
  DEFAULT_PAGE_APPEARANCE,
  normalizePageAppearance,
  type PageAppearance,
} from "@/lib/cms-puck/blocks/appearance";
import { usePuckTheme } from "@/context/cms-puck/PuckThemeContext";
import { PageBackgroundField } from "@/components/cms/theme/PageBackgroundField";
import { PageTextColorField } from "@/components/cms/theme/PageTextColorField";
import { CmsTypographyPicker } from "@/components/cms/theme/CmsTypographyPicker";
import {  resolveThemeDefaultPageTextColor,
  type CmsColor,
} from "@/lib/cms/cms-theme.constants";
import { useCmsNew } from "@/lib/cms/cms-new-i18n";

/**
 * Champ d'apparence par page (`root.props.appearance`).
 */
export function PageAppearanceField({
  value,
  onChange,
}: {
  value: PageAppearance | undefined;
  onChange: (value: PageAppearance) => void;
}) {
  const cmsNew = useCmsNew();
  const theme = usePuckTheme();
  const appearance = normalizePageAppearance(value ?? DEFAULT_PAGE_APPEARANCE);
  const colors: CmsColor[] = theme?.colors ?? [];

  const update = (patch: Partial<PageAppearance>) =>
    onChange({ ...appearance, ...patch });

  const themeDefaultTextHex =
    (theme && resolveThemeDefaultPageTextColor(theme)) || undefined;

  return (
    <div className="space-y-4 p-1">
      <PageBackgroundField
        colors={colors}
        pageBackgroundColorId={appearance.pageBackgroundColorId}
        defaultPageBackgroundHex={theme?.defaultPageBackgroundColor ?? "#FFFFFF"}
        onChange={(pageBackgroundColorId) => update({ pageBackgroundColorId })}
      />
      <PageTextColorField
        colors={colors}
        pageTextColorMode={appearance.pageTextColorMode}
        pageTextColorId={appearance.pageTextColorId}
        themePreviewHex={themeDefaultTextHex}
        onModeChange={(pageTextColorMode) => update({ pageTextColorMode })}
        onColorIdChange={(pageTextColorId) => update({ pageTextColorId })}
      />
      <CmsTypographyPicker
        label={cmsNew("cms.new.auto.label.police_de_la_page", "Police de la page")}
        allowNone
        value={{
          fontFamily: appearance.pageFontFamily,
          fontVariant: appearance.pageFontVariant,
        }}
        inheritDisplayValue={{
          fontFamily: theme?.defaultPageFontFamily ?? null,
          fontVariant: theme?.defaultPageFontVariant ?? null,
        }}
        onChange={(typography) =>
          update({
            pageFontFamily: typography.fontFamily,
            pageFontVariant: typography.fontVariant,
          })
        }
      />
    </div>
  );
}
